import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Trophy, ShieldHalf, ChevronRight, ChevronDown, Globe2, Info, Moon } from "lucide-react";
import { api } from "../lib/api";
import { Flag } from "../components/Flag";
import { Loader, Empty } from "../components/UI";
import { ClubCrest } from "../components/ClubCrest";
import { StatusPill } from "./CupPage";

const CONTAINER = "mx-auto max-w-[1700px] px-4 sm:px-8 lg:px-12";

// Tier identities — one WSTF Europe system, three distinct-but-related looks.
// Accent is never the ONLY signal (each tier also carries a text label + icon).
export const TIER = {
  1: { label: "Tier 1", short: "Champions Cup", accent: "#E5B842", Icon: Crown,
       ring: "from-[#241c08] to-[#070707]", border: "rgba(212,175,55,0.5)", glow: "rgba(212,175,55,0.2)", w: "lg:max-w-[1120px]" },
  2: { label: "Tier 2", short: "European Cup", accent: "#C7CBD1", Icon: Trophy,
       ring: "from-[#191a1d] to-[#070707]", border: "rgba(199,203,209,0.4)", glow: "rgba(199,203,209,0.14)", w: "lg:max-w-[960px]" },
  3: { label: "Tier 3", short: "Challenge Cup", accent: "#3FBF8F", Icon: ShieldHalf,
       ring: "from-[#0e2219] to-[#070707]", border: "rgba(63,191,143,0.4)", glow: "rgba(63,191,143,0.14)", w: "lg:max-w-[820px]" },
};

function TierPyramidCard({ t, season }) {
  const id = TIER[t.tier] || TIER[2];
  const Icon = id.Icon;
  return (
    <div className={`w-full ${id.w} mx-auto`}>
      <Link to={`/continental/${t.tier}`} data-testid={`continental-tier-${t.tier}`}
        className="group relative block rounded-2xl foil-border overflow-hidden bg-gradient-to-r"
        style={{ borderColor: id.border, transition: "transform 0.18s ease, box-shadow 0.18s ease" }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 16px 44px -16px ${id.glow}`; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}>
        <div className={`absolute inset-0 bg-gradient-to-r ${id.ring}`} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(70% 150% at 90% 0%, ${id.glow} 0%, transparent 58%)` }} />
        <div className="relative p-5 sm:p-6 flex items-center gap-4 sm:gap-6">
          <div className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-[#000000] flex items-center justify-center border" style={{ borderColor: id.border }}>
            <Icon className="w-7 h-7 sm:w-8 sm:h-8" style={{ color: id.accent }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: id.accent }}>{id.label} · {id.short}</span>
              <StatusPill status={t.status} />
            </div>
            <div className="font-heading text-xl sm:text-2xl lg:text-3xl font-black uppercase tracking-tight text-slate-50 leading-none mt-1">{t.name}</div>
            <div className="mt-2 flex items-center gap-x-4 gap-y-1 flex-wrap font-mono text-[11px] text-slate-400">
              <span>{t.clubs} clubs</span>
              {season && <span>Season {season}</span>}
              {t.champion && (
                <span className="inline-flex items-center gap-1.5 text-slate-300">
                  <Crown className="w-3.5 h-3.5" style={{ color: id.accent }} />
                  <span className="font-bold" style={{ color: id.accent }}>{t.champion.name}</span> champions
                </span>
              )}
            </div>
          </div>
          <ChevronRight className="hidden sm:block w-6 h-6 text-slate-600 group-hover:translate-x-1 shrink-0" style={{ transition: "transform 0.15s ease", color: id.accent }} />
        </div>
      </Link>
    </div>
  );
}

export default function ContinentalPage() {
  const [tiers, setTiers] = useState(null);
  const [coeff, setCoeff] = useState(null);
  const [season, setSeason] = useState(null);

  useEffect(() => {
    api.get("/continental").then(({ data }) => {
      setTiers(data.tiers);
      const first = data.tiers?.[0]?.id;
      if (first) api.get(`/competitions/${first}`).then(({ data: c }) => setSeason(c?.season?.name)).catch(() => {});
    }).catch(() => setTiers([]));
    api.get("/continental/coefficients").then(({ data }) => setCoeff(data)).catch(() => setCoeff(null));
  }, []);

  if (!tiers) return <Loader label="Loading European competitions…" />;

  const champs = tiers.filter((t) => t.status === "COMPLETED" && t.champion);

  return (
    <div className="animate-fade-up pb-20">
      {/* EUROPE HERO — continental night atmosphere, black/gold primary */}
      <section className="relative overflow-hidden border-b border-[rgba(212,175,55,0.22)] bg-[#000000]">
        <div className="absolute inset-0 mesh-grid opacity-[0.16] pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(80% 120% at 25% -10%, rgba(45,70,120,0.18) 0%, transparent 50%), radial-gradient(90% 130% at 85% 0%, rgba(212,175,55,0.18) 0%, transparent 55%)" }} />
        <div className={`${CONTAINER} relative py-12 sm:py-16`}>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#E5B842] font-mono font-bold mb-3 flex items-center gap-2"><Moon className="w-3.5 h-3.5" /> ESBF · European Club Competitions</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-slate-50 leading-none wsbf-slant-block">European Nights</h1>
          <p className="text-slate-400 mt-3 text-sm sm:text-base max-w-3xl">Three continental tiers, one ArenaBook competitive system. Sanctioned clubs from across the WSTF continental structure chase the European title — quarterfinals and semifinals over two legs on aggregate Team Points, the final a single decisive match.</p>
        </div>
      </section>

      <div className={`${CONTAINER} pt-10`}>
        {/* THREE-TIER PYRAMID */}
        <div className="flex items-center gap-3 mb-2">
          <Trophy className="w-5 h-5 text-[#E5B842]" />
          <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50">The Three-Tier Pyramid</h2>
        </div>
        <p className="text-[11px] font-mono text-slate-500 mb-6">Vertical order reflects prestige / tier level only — there is no promotion or relegation between tiers.</p>

        {tiers.length === 0 ? <Empty label="Continental competitions have not started yet." /> : (
          <div className="space-y-1" data-testid="continental-tiers">
            {tiers.map((t, i) => (
              <div key={t.id}>
                <TierPyramidCard t={t} season={season} />
                {i < tiers.length - 1 && (
                  <div className="flex justify-center py-2 text-slate-700" aria-hidden="true"><ChevronDown className="w-5 h-5" /></div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* EUROPEAN CHAMPIONS MODULE */}
        {champs.length > 0 && (
          <section className="mt-12" data-testid="european-champions">
            <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50 mb-5 flex items-center gap-2"><Crown className="w-5 h-5 text-[#E5B842]" /> European Champions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {champs.map((t) => {
                const id = TIER[t.tier] || TIER[2];
                return (
                  <Link key={t.id} to={`/club/${t.champion.id}`} data-testid={`european-champion-${t.tier}`}
                    className="group relative rounded-xl bg-[#0f0f0f] border p-5 flex flex-col items-center text-center overflow-hidden"
                    style={{ borderColor: id.border, transition: "transform 0.15s ease" }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = ""; }}>
                    <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(80% 120% at 50% 0%, ${id.glow} 0%, transparent 60%)` }} />
                    <span className="relative font-mono text-[10px] font-bold uppercase tracking-[0.22em] mb-3" style={{ color: id.accent }}>{id.label} · {id.short}</span>
                    <ClubCrest club={t.champion} size={56} />
                    <span className="relative font-heading text-lg font-black uppercase tracking-tight text-slate-50 mt-3 leading-snug">{t.champion.name}</span>
                    <span className="relative text-[11px] font-mono text-slate-500 mt-1">Tier {t.tier} Champions</span>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* COUNTRY COEFFICIENTS */}
        {coeff?.coefficients?.length > 0 && (
          <section className="mt-12" data-testid="country-coefficients">
            <h2 className="flex items-center gap-2 font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50 mb-2">
              <Globe2 className="w-5 h-5 text-[#E5B842]" /> Country Coefficients
            </h2>
            {/* Persistent disclaimer — shown WITH the table, never a tooltip */}
            <div data-testid="coefficients-disclaimer" className="flex items-start gap-2 rounded-lg bg-[#140f02]/40 border border-[rgba(212,175,55,0.25)] px-4 py-2.5 mb-2">
              <Info className="w-4 h-4 text-[#E5B842] shrink-0 mt-0.5" />
              <p className="text-xs text-slate-300 font-mono"><b className="text-[#E5B842]">Reference ranking.</b> Country coefficients currently do not affect European slot allocation.</p>
            </div>
            <p className="text-[11px] text-slate-500 mb-4 font-mono">
              Formula {coeff.formula.version}: {coeff.formula.win_points}·Wins + {coeff.formula.draw_points}·Draws + {coeff.formula.tie_win_bonus}·Ties won
            </p>
            <div className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] overflow-hidden">
              <table className="w-full text-sm" data-testid="coefficients-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500 font-mono bg-[#070707]">
                    <th scope="col" className="text-left px-5 py-2.5 w-12">Rank</th>
                    <th scope="col" className="text-left px-2 py-2.5">Country</th>
                    <th scope="col" className="text-right px-2 py-2.5 hidden sm:table-cell">Played</th>
                    <th scope="col" className="text-right px-2 py-2.5 hidden sm:table-cell">Wins</th>
                    <th scope="col" className="text-right px-2 py-2.5 hidden md:table-cell">Draws</th>
                    <th scope="col" className="text-right px-2 py-2.5 hidden md:table-cell">Ties</th>
                    <th scope="col" className="text-right px-5 py-2.5 text-[#E5B842]">Coeff</th>
                  </tr>
                </thead>
                <tbody>
                  {coeff.coefficients.map((c) => (
                    <tr key={c.country_iso3} data-testid={`coeff-row-${c.country_iso3}`} className="border-t border-[#2b2512]/60 hover:bg-[#161616]/60" style={{ transition: "background-color 0.15s ease" }}>
                      <td className="px-5 py-3 font-heading font-black text-slate-200">{c.rank}</td>
                      <td className="px-2 py-3 text-slate-100 font-semibold"><span className="inline-flex items-center gap-1.5">{c.country && <Flag country={c.country} />}{c.country?.name || c.country_iso3}</span></td>
                      <td className="px-2 py-3 text-right text-slate-400 tabular-nums hidden sm:table-cell">{c.matches_played}</td>
                      <td className="px-2 py-3 text-right text-emerald-400 tabular-nums hidden sm:table-cell">{c.match_wins}</td>
                      <td className="px-2 py-3 text-right text-slate-400 tabular-nums hidden md:table-cell">{c.match_draws}</td>
                      <td className="px-2 py-3 text-right text-slate-400 tabular-nums hidden md:table-cell">{c.ties_won}</td>
                      <td className="px-5 py-3 text-right font-heading font-black text-[#E5B842] text-base tabular-nums">{c.coefficient}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 text-[11px] font-mono text-slate-600">Played · Wins · Draws · Ties won · Coeff = coefficient (reference only)</div>
          </section>
        )}
      </div>
    </div>
  );
}
